import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../features/auth/context/AuthContext';
import { usePlanEntitlements } from '../../features/billing/planEntitlements';
import { PermissionCodes, PermissionScopes } from '../../features/auth/permissions';
import { useAssetsModuleVisible } from '../../features/assets/assetsModule';
import { useFieldOperationsModuleVisible } from '../../features/field-operations/fieldOperationsModule';
import { SidebarGlobalSearch } from './SidebarGlobalSearch';

type SidebarProps = {
  collapsed?: boolean;
  onToggle?: () => void;
};

type NavGroupId = 'conversations' | 'ai' | 'admin';

type NavGroupToggleProps = {
  groupId: NavGroupId;
  label: string;
  icon: SidebarIconName;
  isExpanded: boolean;
  isActive: boolean;
  collapsed: boolean;
  onToggle: (groupId: NavGroupId) => void;
};

const NavGroupToggle: React.FC<NavGroupToggleProps> = ({
  groupId,
  label,
  icon,
  isExpanded,
  isActive,
  collapsed,
  onToggle,
}) => (
  <button
    type="button"
    className={`nav-item nav-group-toggle ${isActive ? 'active' : ''} ${isExpanded ? 'expanded' : ''}`}
    onClick={() => onToggle(groupId)}
    aria-expanded={isExpanded}
    title={collapsed ? label : undefined}
  >
    <div className="nav-item-inner">
      <div className="nav-left">
        <span className="nav-icon" aria-hidden><SidebarIcon name={icon} /></span>
        <span className="nav-label">{label}</span>
      </div>
      <span className={`nav-chevron ${isExpanded ? 'nav-chevron-expanded' : ''}`} aria-hidden>›</span>
    </div>
  </button>
);

const isGroupActive = (
  groupId: NavGroupId,
  pathname: string,
): boolean => {
  switch (groupId) {
    case 'conversations':
      return pathname.startsWith('/conversations') || pathname.startsWith('/calls');
    case 'ai':
      return pathname.startsWith('/ai');
    case 'admin':
      return pathname.startsWith('/admin') || pathname.startsWith('/members');
    default:
      return false;
  }
};

const getExpandedGroupsForLocation = (pathname: string): Record<NavGroupId, boolean> => ({
  conversations: isGroupActive('conversations', pathname),
  ai: isGroupActive('ai', pathname),
  admin: isGroupActive('admin', pathname),
});

type SidebarIconName =
  | 'dashboard'
  | 'contacts'
  | 'assets'
  | 'products'
  | 'services'
  | 'invoices'
  | 'payments'
  | 'conversations'
  | 'calendar'
  | 'rostering'
  | 'fieldOperations'
  | 'consultations'
  | 'tasks'
  | 'knowledge'
  | 'admin'
  | 'allConversations'
  | 'directMessages'
  | 'channels'
  | 'external'
  | 'calls'
  | 'knowledgeBase'
  | 'subagents'
  | 'orchestrators'
  | 'tools'
  | 'configuration'
  | 'aiActivity'
  | 'teamMembers'
  | 'company'
  | 'connectors'
  | 'automations'
  | 'roles'
  | 'security';

const SidebarIcon: React.FC<{ name: SidebarIconName }> = ({ name }) => {
  switch (name) {
    case 'dashboard':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M4 12.5h6.2V20H4v-7.5zm9.1-8.5h6.9V20h-6.9V4zM4 4h6.2v5.5H4V4z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
        </svg>
      );
    case 'contacts':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M7 19c0-2.8 2.24-5 5-5s5 2.2 5 5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
          <circle cx="12" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.9" />
        </svg>
      );
    case 'assets':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M12 3l7 4v10l-7 4-7-4V7l7-4z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
          <path d="M5 7l7 4 7-4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 11v10" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
      );
    case 'products':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M4.5 8.2 12 4l7.5 4.2v7.6L12 20l-7.5-4.2V8.2z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
          <path d="M12 12.2V20M4.5 8.2 12 12.2 19.5 8.2" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
        </svg>
      );
    case 'services':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <circle cx="8" cy="8" r="2.4" stroke="currentColor" strokeWidth="1.9" />
          <circle cx="16" cy="8" r="2.4" stroke="currentColor" strokeWidth="1.9" />
          <path d="M4.8 18.5c.7-2.4 2.7-4 5.2-4s4.5 1.6 5.2 4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
          <path d="M14.2 14.8c.7-.5 1.6-.8 2.6-.8 2.5 0 4.5 1.6 5.2 4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
      );
    case 'invoices':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M7 3.8h10A1.7 1.7 0 0 1 18.7 5.5v14.2L16 17.8l-2.7 1.9-2.6-1.9-2.7 1.9V5.5A1.7 1.7 0 0 1 7 3.8z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
          <path d="M9.2 8.2h5.6M9.2 11.4h5.6M9.2 14.6h3.4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
      );
    case 'payments':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <rect x="3.5" y="6" width="17" height="12" rx="2.2" stroke="currentColor" strokeWidth="1.9" />
          <path d="M3.5 10h17" stroke="currentColor" strokeWidth="1.9" />
          <path d="M8 14.2h4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
      );
    case 'conversations':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M5 6.5A2.5 2.5 0 0 1 7.5 4h9A2.5 2.5 0 0 1 19 6.5v7A2.5 2.5 0 0 1 16.5 16H11l-4 3v-3H7.5A2.5 2.5 0 0 1 5 13.5v-7z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
          <path d="M9 9.5h6M9 12h4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
      );
    case 'calendar':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <rect x="4" y="5" width="16" height="15" rx="3" stroke="currentColor" strokeWidth="1.9" />
          <path d="M8 3.5v3M16 3.5v3M4 9h16" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
      );
    case 'rostering':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <rect x="4" y="5" width="16" height="14" rx="3" stroke="currentColor" strokeWidth="1.9" />
          <path d="M4 10h16M9 5v4M15 5v4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
          <path d="M8 14h3M13 14h3M8 17h3M13 17h3" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
      );
    case 'fieldOperations':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <circle cx="12" cy="11" r="7" stroke="currentColor" strokeWidth="1.9" />
          <path d="M12 4v2M12 16v2M4 11h2M18 11h2" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
          <circle cx="12" cy="11" r="2.2" fill="currentColor" />
        </svg>
      );
    case 'consultations':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M8 4.5h8l3 3V18a2 2 0 0 1-2 2H8a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
          <path d="M16 4.5V8h3" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
          <path d="M9 11h6M9 14h6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
      );
    case 'tasks':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" strokeWidth="1.9" />
          <path d="M8 12l2.5 2.5L16 9" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'knowledge':
    case 'knowledgeBase':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M5.5 5.5A2.5 2.5 0 0 1 8 3h9.5a1.5 1.5 0 0 1 1.5 1.5V18a1.5 1.5 0 0 1-1.5 1.5H8A2.5 2.5 0 0 0 5.5 22V5.5z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
          <path d="M8 6h8M8 10h8" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
      );
    case 'admin':
    case 'configuration':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.9" />
          <path d="M19 12a7 7 0 0 0-.08-1l2.02-1.58-1.9-3.28-2.44.98a7.2 7.2 0 0 0-1.72-1L14.5 3h-5l-.38 3.12a7.2 7.2 0 0 0-1.72 1l-2.44-.98-1.9 3.28L5.08 11A7 7 0 0 0 5 12c0 .34.03.67.08 1l-2.02 1.58 1.9 3.28 2.44-.98c.52.42 1.1.76 1.72 1L9.5 21h5l.38-3.12c.62-.24 1.2-.58 1.72-1l2.44.98 1.9-3.28L18.92 13c.05-.33.08-.66.08-1z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'allConversations':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M5 7.5A2.5 2.5 0 0 1 7.5 5h9A2.5 2.5 0 0 1 19 7.5v5A2.5 2.5 0 0 1 16.5 15H11l-3.5 2.5V15H7.5A2.5 2.5 0 0 1 5 12.5v-5z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
          <path d="M8.5 9h7M8.5 11.5h4.5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
      );
    case 'directMessages':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M4.5 7.5A2.5 2.5 0 0 1 7 5h10a2.5 2.5 0 0 1 2.5 2.5v9A2.5 2.5 0 0 1 17 19H7a2.5 2.5 0 0 1-2.5-2.5v-9z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
          <path d="M5.5 8l6.5 5 6.5-5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'channels':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M8 5v14M16 5v14M5 9h14M5 15h14" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
      );
    case 'external':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M10 5H7a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M13 5h6v6M11 13l8-8" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'calls':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M6.5 4h2.2l1.5 3.6-1.8 1.4a11.5 11.5 0 0 0 5.6 5.6l1.4-1.8 3.6 1.5v2.2a2 2 0 0 1-2.2 2A15.5 15.5 0 0 1 4.5 6.2 2 2 0 0 1 6.5 4Z" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'subagents':
    case 'orchestrators':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <circle cx="6.5" cy="7" r="2.2" stroke="currentColor" strokeWidth="1.9" />
          <circle cx="17.5" cy="7" r="2.2" stroke="currentColor" strokeWidth="1.9" />
          <circle cx="12" cy="17" r="2.2" stroke="currentColor" strokeWidth="1.9" />
          <path d="M8.3 8.5l2.2 6.2M15.7 8.5l-2.2 6.2M8.7 7h6.6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
      );
    case 'tools':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M14.5 5.5l4 4-8.2 8.2-4.8 1 1-4.8L14.5 5.5z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
          <path d="M13 7l4 4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
      );
    case 'aiActivity':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M4 13h3l2-5 3 10 2-5h6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'teamMembers':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <circle cx="9" cy="8" r="2.8" stroke="currentColor" strokeWidth="1.9" />
          <path d="M3.8 18.5c.6-2.7 2.8-4.3 5.2-4.3s4.6 1.6 5.2 4.3" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
          <circle cx="16.5" cy="8.5" r="2.2" stroke="currentColor" strokeWidth="1.9" />
          <path d="M14.8 14.2c1.5.4 2.8 1.5 3.4 3.3" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
      );
    case 'company':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M4 20V8.5L12 4l8 4.5V20" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
          <path d="M9 20v-6h6v6" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
          <path d="M9 10h.01M12 10h.01M15 10h.01M9 13h.01M12 13h.01M15 13h.01" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
        </svg>
      );
    case 'connectors':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M9.5 14.5l5-5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
          <path d="M11.2 7.3l1.5-1.5a3.5 3.5 0 0 1 5 5l-1.5 1.5M12.8 16.7l-1.5 1.5a3.5 3.5 0 0 1-5-5l1.5-1.5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'automations':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M13.2 3.5L6.5 13h4.7l-.9 7.5L17.5 11h-4.7l.4-7.5z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
        </svg>
      );
    case 'roles':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M7 11V8.5A5 5 0 0 1 17 8.5V11" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
          <rect x="5" y="11" width="14" height="9" rx="2.5" stroke="currentColor" strokeWidth="1.9" />
          <circle cx="12" cy="15.5" r="1.4" fill="currentColor" />
        </svg>
      );
    case 'security':
      return (
        <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M12 3.5l7 3v5.2c0 4.4-2.9 7.6-7 9.3-4.1-1.7-7-4.9-7-9.3V6.5l7-3z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
          <path d="M9.2 12.1l1.9 1.9 3.8-3.8" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    default:
      return null;
  }
};

const NavSubItemContent: React.FC<{ icon: SidebarIconName; label: string }> = ({ icon, label }) => (
  <span className="nav-sub-item-main">
    <span className="nav-sub-icon" aria-hidden><SidebarIcon name={icon} /></span>
    <span className="nav-sub-label">{label}</span>
  </span>
);

export const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, onToggle }) => {
  const { t } = useTranslation(['nav', 'header']);
  const { hasPermission } = useAuth();
  const { hasFeature } = usePlanEntitlements();
  const location = useLocation();
  const isAssetsModuleVisible = useAssetsModuleVisible();
  const isFieldOperationsModuleVisible = useFieldOperationsModuleVisible();

  const canViewContacts = hasPermission(PermissionScopes.contactsRead) && hasFeature('contacts');
  const canViewAssets = hasPermission(PermissionScopes.assetsRead)
    && (isAssetsModuleVisible || hasPermission(PermissionCodes.assetsUpdate));
  const canViewProducts = hasPermission(PermissionScopes.productsRead) && hasFeature('products');
  const canViewServices = hasPermission(PermissionScopes.servicesRead) && hasFeature('services');
  const canViewInvoices = hasPermission(PermissionScopes.invoicesRead) && hasFeature('invoices');
  const canViewPayments = hasPermission(PermissionScopes.paymentsRead) && hasFeature('invoices');
  const canViewConversations = hasPermission(PermissionScopes.conversationsRead) && hasFeature('inbox');
  const canViewCalls = hasPermission(PermissionScopes.conversationsRead) && hasFeature('calls');
  const canViewCalendar = hasPermission(PermissionScopes.calendarRead) && hasFeature('calendar');
  const canViewRostering = hasPermission(PermissionScopes.rosteringRead);
  // Admins keep the entry while the module is off so they can switch it on.
  const canViewFieldOperations = (isFieldOperationsModuleVisible && hasPermission(PermissionScopes.fieldOperationsCheckIn))
    || hasPermission(PermissionScopes.fieldOperationsSettings);
  const canViewConsultations = hasPermission(PermissionScopes.consultationsRead) && hasFeature('consultations');
  const canViewTasks = hasPermission(PermissionScopes.tasksRead) && hasFeature('tasks');
  const canViewReminderEngine = hasPermission(PermissionScopes.remindersRead);
  const canViewKnowledge = hasPermission(PermissionScopes.knowledgeRead) && hasFeature('knowledgeBase');
  const canViewAiOrchestrators = hasPermission(PermissionScopes.aiOrchestratorsRead) && hasFeature('aiEmployees');
  const canViewApiTools = hasPermission(PermissionScopes.apiToolsRead) && hasFeature('api');
  const canViewDataTools = hasPermission(PermissionCodes.knowledgeManage) && hasFeature('knowledgeBase');
  const canViewMcpTools = hasPermission(PermissionScopes.mcpConnectorsRead) && hasFeature('mcpTools');
  const canViewAiConfig = hasPermission(PermissionScopes.aiConfigRead) && hasFeature('aiEmployees');
  const canViewTools = canViewApiTools || canViewDataTools || canViewMcpTools || canViewAiConfig;
  const canViewAiActivity = hasPermission([PermissionCodes.adminAccess]);
  const canViewAiGroup = canViewKnowledge || canViewAiOrchestrators || canViewTools || canViewAiConfig || canViewAiActivity;
  const canViewDashboard = (hasPermission(PermissionScopes.conversationsRead) && hasFeature('basicAnalytics'))
    || canViewConsultations
    || canViewTasks;

  const canViewTeamMembers = hasPermission(PermissionScopes.employeesRead);
  const canViewCompany = hasPermission(PermissionScopes.companiesRead);
  const canViewConnectors = hasPermission(PermissionScopes.connectorsRead);
  const canViewAutomations = hasPermission(PermissionScopes.conversationsRead) && hasFeature('automations');
  const canViewRoles = hasPermission(PermissionScopes.rolesRead);
  const canViewSecurity = hasPermission(PermissionCodes.adminAccess);
  const canViewAdminGroup = canViewTeamMembers || canViewCompany || canViewConnectors || canViewRoles || canViewSecurity;
  const [expandedGroups, setExpandedGroups] = React.useState<Record<NavGroupId, boolean>>(() =>
    getExpandedGroupsForLocation(location.pathname),
  );

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    isActive ? 'nav-item active' : 'nav-item';
  const isAiKnowledgePath = location.pathname.startsWith('/ai/knowledge') || location.pathname === '/knowledge';
  const isAiOrchestratorsPath = location.pathname.startsWith('/ai/subagents')
    || location.pathname.startsWith('/ai/orchestrators');
  const isAiToolsPath = location.pathname.startsWith('/ai/tools');
  const isAiConfigPath = location.pathname.startsWith('/ai/config') || location.pathname === '/settings/ai';
  const isAiActivityPath = location.pathname.startsWith('/ai/activity');
  const isConversationsPath = location.pathname.startsWith('/conversations');
  const conversationView = new URLSearchParams(location.search).get('view');

  const toggleNavGroup = React.useCallback((groupId: NavGroupId) => {
    setExpandedGroups(current => ({
      ...current,
      [groupId]: !current[groupId],
    }));
  }, []);

  React.useEffect(() => {
    const autoExpanded = getExpandedGroupsForLocation(location.pathname);
    setExpandedGroups(current => ({
      ...current,
      ...(autoExpanded.conversations ? { conversations: true } : {}),
      ...(autoExpanded.ai ? { ai: true } : {}),
      ...(autoExpanded.admin ? { admin: true } : {}),
    }));
  }, [location.pathname, location.search]);
  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-brand">
        <div className="brand-logo">{t('brandInitials', { ns: 'header' })}</div>
        <div className="sidebar-brand-copy">
          <span className="sidebar-brand-name">{t('brandName', { ns: 'header' })}</span>
        </div>
      </div>
      <SidebarGlobalSearch collapsed={collapsed} />
      <nav className="sidebar-nav">
        {canViewDashboard ? (
        <NavLink to="/dashboard" className={linkClass} title={collapsed ? t('dashboard') : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="dashboard" /></span>
              <span className="nav-label">{t('dashboard')}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {canViewContacts ? (
        <NavLink to="/contacts" className={linkClass} end title={collapsed ? t('contacts') : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="contacts" /></span>
              <span className="nav-label">{t('contacts')}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {canViewAssets ? (
        <NavLink to="/assets" className={linkClass} title={collapsed ? t('assets') : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="assets" /></span>
              <span className="nav-label">{t('assets')}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {canViewProducts ? (
        <NavLink to="/products" className={linkClass} title={collapsed ? t('products') : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="products" /></span>
              <span className="nav-label">{t('products')}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {canViewServices ? (
        <NavLink to="/services" className={linkClass} title={collapsed ? t('services') : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="services" /></span>
              <span className="nav-label">{t('services')}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {canViewInvoices ? (
        <NavLink to="/invoices" className={linkClass} title={collapsed ? t('invoices') : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="invoices" /></span>
              <span className="nav-label">{t('invoices')}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {canViewPayments ? (
        <NavLink to="/payments" className={linkClass} title={collapsed ? t('payments') : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="payments" /></span>
              <span className="nav-label">{t('payments')}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {canViewConversations ? (
        <div className={`nav-group ${expandedGroups.conversations ? 'expanded' : 'collapsed'}`} title={collapsed ? t('conversations') : undefined}>
          <NavGroupToggle
            groupId="conversations"
            label={t('conversations')}
            icon="conversations"
            isExpanded={expandedGroups.conversations}
            isActive={isGroupActive('conversations', location.pathname)}
            collapsed={collapsed}
            onToggle={toggleNavGroup}
          />

          {!collapsed ? (
          <div className="nav-sub-items" aria-hidden={!expandedGroups.conversations}>
          <div className="nav-sub-items-inner">
          <NavLink
            to="/conversations?view=all"
            className={() => (isConversationsPath && conversationView !== 'direct' && conversationView !== 'channels' && conversationView !== 'external' ? 'nav-sub-item active' : 'nav-sub-item')}
            title={collapsed ? t('allConversations') : undefined}
            tabIndex={expandedGroups.conversations ? undefined : -1}
          >
            <NavSubItemContent icon="allConversations" label={t('allConversations')} />
          </NavLink>

          <NavLink
            to="/conversations?view=direct"
            className={() => (isConversationsPath && conversationView === 'direct' ? 'nav-sub-item active' : 'nav-sub-item')}
            title={collapsed ? t('directMessages') : undefined}
            tabIndex={expandedGroups.conversations ? undefined : -1}
          >
            <NavSubItemContent icon="directMessages" label={t('directMessages')} />
          </NavLink>

          <NavLink
            to="/conversations?view=channels"
            className={() => (isConversationsPath && conversationView === 'channels' ? 'nav-sub-item active' : 'nav-sub-item')}
            title={collapsed ? t('channels') : undefined}
            tabIndex={expandedGroups.conversations ? undefined : -1}
          >
            <NavSubItemContent icon="channels" label={t('channels')} />
          </NavLink>

          <NavLink
            to="/conversations?view=external"
            className={() => (isConversationsPath && conversationView === 'external' ? 'nav-sub-item active' : 'nav-sub-item')}
            title={collapsed ? t('external') : undefined}
            tabIndex={expandedGroups.conversations ? undefined : -1}
          >
            <NavSubItemContent icon="external" label={t('external')} />
          </NavLink>

          {canViewCalls ? (
          <NavLink
            to="/calls"
            className={({ isActive }) => (isActive ? 'nav-sub-item active' : 'nav-sub-item')}
            title={collapsed ? t('calls') : undefined}
            tabIndex={expandedGroups.conversations ? undefined : -1}
          >
            <NavSubItemContent icon="calls" label={t('calls')} />
          </NavLink>
          ) : null}
          </div>
          </div>
          ) : null}
        </div>
        ) : null}

        {(canViewCalendar || canViewRostering || canViewFieldOperations) ? (
        <div className="nav-group" title={collapsed ? t('calendar') : undefined}>
          {canViewCalendar ? (
          <NavLink to="/calendar" className={linkClass} title={collapsed ? t('calendar') : undefined}>
            <div className="nav-item-inner">
              <div className="nav-left">
                <span className="nav-icon" aria-hidden><SidebarIcon name="calendar" /></span>
                <span className="nav-label">{t('calendar')}</span>
              </div>
            </div>
          </NavLink>
          ) : null}
          {canViewRostering ? (
          <NavLink to="/rostering" className={linkClass} title={collapsed ? t('rostering') : undefined}>
            <div className="nav-item-inner">
              <div className="nav-left">
                <span className="nav-icon" aria-hidden><SidebarIcon name="rostering" /></span>
                <span className="nav-label">{t('rostering')}</span>
              </div>
            </div>
          </NavLink>
          ) : null}
          {canViewFieldOperations ? (
          <NavLink to="/field-operations" className={linkClass} title={collapsed ? t('fieldOperations') : undefined}>
            <div className="nav-item-inner">
              <div className="nav-left">
                <span className="nav-icon" aria-hidden><SidebarIcon name="fieldOperations" /></span>
                <span className="nav-label">{t('fieldOperations')}</span>
              </div>
            </div>
          </NavLink>
          ) : null}
        </div>
        ) : null}

        {canViewConsultations ? (
        <NavLink to="/consultations" className={linkClass} title={collapsed ? t('consultations') : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="consultations" /></span>
              <span className="nav-label">{t('consultations')}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {canViewTasks ? (
        <NavLink to="/tasks" className={linkClass} title={collapsed ? t('tasks') : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="tasks" /></span>
              <span className="nav-label">{t('tasks')}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {canViewReminderEngine ? (
        <NavLink to="/reminder-engine/definitions" className={linkClass} title={collapsed ? t('reminderEngine') : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="tasks" /></span>
              <span className="nav-label">{t('reminderEngine')}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {canViewAiGroup ? (
        <div className={`nav-group ${expandedGroups.ai ? 'expanded' : 'collapsed'}`} title={collapsed ? t('ai') : undefined}>
          <NavGroupToggle
            groupId="ai"
            label={t('ai')}
            icon="knowledge"
            isExpanded={expandedGroups.ai}
            isActive={isGroupActive('ai', location.pathname)}
            collapsed={collapsed}
            onToggle={toggleNavGroup}
          />

          {!collapsed ? (
          <div className="nav-sub-items" aria-hidden={!expandedGroups.ai}>
          <div className="nav-sub-items-inner">
          {canViewKnowledge ? (
          <NavLink
            to="/ai/knowledge"
            className={() => isAiKnowledgePath ? 'nav-sub-item active' : 'nav-sub-item'}
            title={collapsed ? t('knowledgeBase') : undefined}
            tabIndex={expandedGroups.ai ? undefined : -1}
          >
            <NavSubItemContent icon="knowledgeBase" label={t('knowledgeBase')} />
          </NavLink>
          ) : null}

          {canViewAiOrchestrators ? (
          <NavLink
            to="/ai/subagents"
            className={() => isAiOrchestratorsPath ? 'nav-sub-item active' : 'nav-sub-item'}
            title={collapsed ? t('subagents') : undefined}
            tabIndex={expandedGroups.ai ? undefined : -1}
          >
            <NavSubItemContent icon="subagents" label={t('subagents')} />
          </NavLink>
          ) : null}

          {canViewTools ? (
          <NavLink
            to="/ai/tools"
            className={() => isAiToolsPath ? 'nav-sub-item active' : 'nav-sub-item'}
            title={collapsed ? t('tools') : undefined}
            tabIndex={expandedGroups.ai ? undefined : -1}
          >
            <NavSubItemContent icon="tools" label={t('tools')} />
          </NavLink>
          ) : null}

          {canViewAiConfig ? (
          <NavLink
            to="/ai/config"
            className={() => isAiConfigPath ? 'nav-sub-item active' : 'nav-sub-item'}
            title={collapsed ? t('configuration') : undefined}
            tabIndex={expandedGroups.ai ? undefined : -1}
          >
            <NavSubItemContent icon="configuration" label={t('configuration')} />
          </NavLink>
          ) : null}

          {canViewAiActivity ? (
          <NavLink
            to="/ai/activity"
            className={() => isAiActivityPath ? 'nav-sub-item active' : 'nav-sub-item'}
            title={collapsed ? t('aiActivity') : undefined}
            tabIndex={expandedGroups.ai ? undefined : -1}
          >
            <NavSubItemContent icon="aiActivity" label={t('aiActivity')} />
          </NavLink>
          ) : null}
          </div>
          </div>
          ) : null}
        </div>
        ) : null}

        {canViewAutomations ? (
        <NavLink to="/automations" className={linkClass} title={collapsed ? t('automations') : undefined}>
          <div className="nav-item-inner">
            <div className="nav-left">
              <span className="nav-icon" aria-hidden><SidebarIcon name="automations" /></span>
              <span className="nav-label">{t('automations')}</span>
            </div>
          </div>
        </NavLink>
        ) : null}

        {canViewAdminGroup ? (
        <div className={`nav-group ${expandedGroups.admin ? 'expanded' : 'collapsed'}`} title={collapsed ? t('admin') : undefined}>
          <NavGroupToggle
            groupId="admin"
            label={t('admin')}
            icon="admin"
            isExpanded={expandedGroups.admin}
            isActive={isGroupActive('admin', location.pathname)}
            collapsed={collapsed}
            onToggle={toggleNavGroup}
          />

          {!collapsed ? (
          <div className="nav-sub-items" aria-hidden={!expandedGroups.admin}>
          <div className="nav-sub-items-inner">
          {canViewTeamMembers ? (
          <NavLink to="/members" className={({ isActive }) => isActive ? 'nav-sub-item active' : 'nav-sub-item'} title={collapsed ? t('teamMembers') : undefined} tabIndex={expandedGroups.admin ? undefined : -1}>
            <NavSubItemContent icon="teamMembers" label={t('teamMembers')} />
          </NavLink>
          ) : null}

          {canViewCompany ? (
          <NavLink to="/admin/companies" className={({ isActive }) => isActive ? 'nav-sub-item active' : 'nav-sub-item'} title={collapsed ? t('company') : undefined} tabIndex={expandedGroups.admin ? undefined : -1}>
            <NavSubItemContent icon="company" label={t('company')} />
          </NavLink>
          ) : null}

          {canViewConnectors ? (
          <NavLink to="/admin/connectors" className={({ isActive }) => isActive ? 'nav-sub-item active' : 'nav-sub-item'} title={collapsed ? t('connectors', { ns: 'admin' }) : undefined} tabIndex={expandedGroups.admin ? undefined : -1}>
            <NavSubItemContent icon="connectors" label={t('connectors', { ns: 'admin' })} />
          </NavLink>
          ) : null}

          {canViewRoles ? (
          <NavLink to="/admin/roles" className={({ isActive }) => isActive ? 'nav-sub-item active' : 'nav-sub-item'} title={collapsed ? t('roles') : undefined} tabIndex={expandedGroups.admin ? undefined : -1}>
            <NavSubItemContent icon="roles" label={t('roles')} />
          </NavLink>
          ) : null}

          {canViewSecurity ? (
          <NavLink to="/admin/security" className={({ isActive }) => isActive ? 'nav-sub-item active' : 'nav-sub-item'} title={collapsed ? t('security') : undefined} tabIndex={expandedGroups.admin ? undefined : -1}>
            <NavSubItemContent icon="security" label={t('security')} />
          </NavLink>
          ) : null}
          </div>
          </div>
          ) : null}
        </div>
        ) : null}
      </nav>
      <div className="sidebar-bottombar">
        <button
          className="sidebar-toggle"
          onClick={onToggle}
          title={collapsed ? t('expandSidebar') : t('collapseSidebar')}
        >
          {collapsed ? '»' : '«'}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;